import React, { useState } from "react";
import { Alert, Button, Card, Modal } from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import { AppLayout } from "../layout";
import { useAuth } from "../providers/AuthProvider";
import { useDeleteMyAccount, useExportMyData } from "./usePrivacy";

/**
 * TASK-19 · "Mis datos personales" — derechos del titular (Ley 1581):
 * exportar datos y eliminar cuenta. Route: /:slug/app/privacidad.
 */
function PrivacyPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [showConfirm, setShowConfirm] = useState(false);

  const exportData = useExportMyData();
  const deleteAccount = useDeleteMyAccount();

  function handleExport() {
    exportData.mutate(undefined, {
      onSuccess: (data) =>
        toast.success(
          data?.message || "Solicitud enviada. Recibirás tu descarga en breve."
        ),
      onError: () => toast.error("No se pudo solicitar la exportación."),
    });
  }

  function handleDelete() {
    deleteAccount.mutate("", {
      onSuccess: async (data) => {
        setShowConfirm(false);
        toast.success(data?.message || "Solicitud de eliminación registrada.");
        await logout();
        navigate(`/${slug}/login`);
      },
      onError: () => {
        setShowConfirm(false);
        toast.error("No se pudo registrar la solicitud.");
      },
    });
  }

  return (
    <AppLayout>
      <Toaster position="top-right" />
      <div className="container-fluid px-3" style={{ maxWidth: 640 }}>
        <h1 className="h5 my-3">Mis datos personales</h1>

        <Card className="mb-3">
          <Card.Body>
            <Card.Title as="h2" className="h6">
              Exportar mis datos
            </Card.Title>
            <p className="text-muted small mb-3">
              Descarga una copia de los datos personales que tu academia
              almacena sobre ti (derecho de acceso, Ley 1581).
            </p>
            <Button
              variant="outline-primary"
              onClick={handleExport}
              disabled={exportData.isPending}
            >
              {exportData.isPending ? "Solicitando…" : "Exportar mis datos"}
            </Button>
          </Card.Body>
        </Card>

        <Card className="mb-3 border-danger">
          <Card.Body>
            <Card.Title as="h2" className="h6 text-danger">
              Eliminar mi cuenta
            </Card.Title>
            <p className="text-muted small mb-3">
              Solicita la eliminación de tu cuenta y datos (derecho de
              supresión). Se completará tras un periodo de gracia.
            </p>
            <Button variant="outline-danger" onClick={() => setShowConfirm(true)}>
              Eliminar mi cuenta
            </Button>
          </Card.Body>
        </Card>

        <p className="small text-muted">
          Consulta nuestra{" "}
          <Link to="/politica-privacidad">política de privacidad</Link>.
        </p>
      </div>

      <Modal
        show={showConfirm}
        onHide={() => setShowConfirm(false)}
        aria-labelledby="confirm-delete-title"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title id="confirm-delete-title" className="h6">
            Confirmar eliminación
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning" className="mb-0">
            ¿Seguro que deseas solicitar la eliminación de tu cuenta? Cerrarás
            sesión y la eliminación se completará tras el periodo de gracia.
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirm(false)}>
            Cancelar
          </Button>
          <Button
            variant="danger"
            onClick={handleDelete}
            disabled={deleteAccount.isPending}
          >
            {deleteAccount.isPending ? "Procesando…" : "Sí, eliminar"}
          </Button>
        </Modal.Footer>
      </Modal>
    </AppLayout>
  );
}

export { PrivacyPage };
